/**
 * The steps of the capacity bridge (PRD 10.1, Design Review V-10; Phase 21), shared by the
 * server read model and the browser client so the two cannot disagree on what a step is called,
 * which bridge field it reads, how it is drawn, or who its contributors are.
 *
 * The bridge is the summary's identity, read as a waterfall:
 *
 *     gross - time_off - nonproject - recurring - committed_explicit - committed_inferred - pipeline = headroom
 *
 * The first step is the whole (gross capacity), the six after it are deductions, and the last is
 * what remains (headroom; below zero, the overload). The five deduction steps after time off are
 * the ribbon's five layers in the ribbon's stack order, wearing the ribbon's hue slots and fills,
 * so a reader who has just checked a layer value on the ribbon finds the same number in the same
 * colour one card down (this is what the Phase 21 gate checks). Gross and time off are not
 * demand and wear no layer hue: gross is drawn in the strong ink as the anchor bar, time off as
 * an outlined bar (capacity that is not there). Headroom is a threshold state, so it wears the
 * reserved status colour of the window's band, always with the icon and the word.
 *
 * `contributors` says how a step breaks down when clicked: people for the capacity steps and
 * the non-project load (scoped, TRD 5.2), projects for the recurring and committed steps,
 * opportunities for the pipeline, and services for the headroom (where the remaining hours sit,
 * or which roles are over).
 */
import { LAYERS } from './layers.js';
import { col } from './table.js';

/** How a step's contributors are grouped. */
export const CONTRIBUTOR_GROUPS = Object.freeze(['person', 'project', 'opportunity', 'service']);

const layerBy = new Map(LAYERS.map((l) => [l.key, l]));
const layerStep = (key, field, contributors) => {
  const layer = layerBy.get(key);
  return Object.freeze({
    key,
    label: layer.label,
    field,
    kind: 'deduction',
    layer: key,
    token: layer.token,
    fill: layer.fill,
    contributors,
  });
};

/** The steps, left to right as the waterfall draws them. */
export const BRIDGE_STEPS = Object.freeze([
  Object.freeze({
    key: 'gross',
    label: 'Gross capacity',
    field: 'gross_hours',
    kind: 'start',
    layer: null,
    token: '--ink-strong',
    fill: 'solid',
    contributors: 'person',
  }),
  Object.freeze({
    key: 'time_off',
    label: 'Time off',
    field: 'time_off_hours',
    kind: 'deduction',
    layer: null,
    token: '--ink-muted',
    fill: 'outline',
    contributors: 'person',
  }),
  layerStep('nonproject', 'nonproject_hours', 'person'),
  layerStep('recurring', 'recurring_hours', 'project'),
  layerStep('committed_explicit', 'committed_explicit_hours', 'project'),
  layerStep('committed_inferred', 'committed_inferred_hours', 'project'),
  layerStep('pipeline', 'pipeline_hours', 'opportunity'),
  Object.freeze({
    key: 'headroom',
    label: 'Headroom',
    field: 'headroom_hours',
    kind: 'result',
    layer: null,
    token: '--status-healthy',
    fill: 'solid',
    contributors: 'service',
  }),
]);

export const BRIDGE_STEP_KEYS = Object.freeze(BRIDGE_STEPS.map((s) => s.key));

/** The deduction steps, in order: what stands between gross capacity and headroom. */
export const DEDUCTION_KEYS = Object.freeze(
  BRIDGE_STEPS.filter((s) => s.kind === 'deduction').map((s) => s.key),
);

const BY_KEY = new Map(BRIDGE_STEPS.map((s) => [s.key, s]));

/** The step with this key, or null. */
export function bridgeStep(key) {
  return BY_KEY.get(key) ?? null;
}

/**
 * The step vocabulary a response carries, with the pipeline label resolved for the read mode
 * (the same rows are weighted or unweighted hours; `none` keeps the step at zero as
 * "Pipeline (excluded)" so the chart and the table keep their shape across the toggle).
 * @param {'weighted' | 'unweighted' | 'none'} pipelineMode
 */
export function bridgeVocabulary(pipelineMode = 'weighted') {
  return BRIDGE_STEPS.map((s) => ({
    key: s.key,
    label:
      s.key === 'pipeline'
        ? pipelineMode === 'none'
          ? 'Pipeline (excluded)'
          : `Pipeline (${pipelineMode === 'unweighted' ? 'unweighted' : 'weighted'})`
        : s.label,
    field: s.field,
    kind: s.kind,
    layer: s.layer,
    token: s.token,
    fill: s.fill,
    contributors: s.contributors,
  }));
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * The waterfall geometry of one bridge (a week's, or the window total): for every step its
 * hours, the running total before and after it, and the [from, to] span its bar covers on the
 * value axis. The anchor bar runs from zero to gross; each deduction hangs from the running
 * total down by its hours; the result bar runs from zero to the headroom, which is below zero
 * when demand exceeds capacity. `sum_check` is the running total after the last deduction, which
 * equals `headroom_hours` when the bridge adds up (the tests hold it; the chart draws the
 * result bar from the bridge's own headroom, so a discrepancy would show as a gap).
 *
 * @param {Record<string, number>} bridge  a summary bridge (`gross_hours`, `time_off_hours`, …)
 */
export function waterfall(bridge) {
  let running = 0;
  const bars = BRIDGE_STEPS.map((s) => {
    const hours = round2(Number(bridge?.[s.field]) || 0);
    if (s.kind === 'start') {
      running = hours;
      return { key: s.key, hours, before: 0, after: running, from: 0, to: hours };
    }
    if (s.kind === 'deduction') {
      const before = running;
      running = round2(running - hours);
      return {
        key: s.key,
        hours,
        before,
        after: running,
        from: Math.min(before, running),
        to: Math.max(before, running),
      };
    }
    return {
      key: s.key,
      hours,
      before: running,
      after: hours,
      from: Math.min(0, hours),
      to: Math.max(0, hours),
    };
  });
  const result = bars.at(-1);
  return {
    bars,
    sum_check: round2(running),
    headroom_hours: result.hours,
    adds_up: Math.abs(round2(running) - result.hours) < 0.005 + 0.005 * BRIDGE_STEPS.length,
    min: Math.min(0, ...bars.map((b) => b.from)),
    max: Math.max(0, ...bars.map((b) => b.to)),
  };
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings. The contributor table's columns follow the step: who or what
// a step is made of differs, and so do the columns worth showing.

/**
 * The waterfall's rows: one per step, with the running total and the share of gross the table
 * shows beside it. Shared with the server's CSV, so the file is the table (Phase 37).
 *
 * @param {object} data  a `GET /forecast/bridge` payload (or one week of it)
 */
export function bridgeStepRows(data) {
  const gross = data?.totals?.gross_hours ?? data?.gross_hours ?? 0;
  const bars = new Map(waterfall(data?.totals ?? data).bars.map((b) => [b.key, b]));
  return (data?.steps ?? []).map((step) => {
    const bar = bars.get(step.key);
    return {
      key: step.key,
      label: step.label,
      kind: step.kind,
      group: step.contributors ?? null,
      hours: bar?.hours ?? 0,
      signed_hours: step.kind === 'deduction' ? -(bar?.hours ?? 0) : (bar?.hours ?? 0),
      after: bar?.after ?? 0,
      share_of_gross: gross > 0 ? (bar?.hours ?? 0) / gross : null,
    };
  });
}

/** The waterfall itself: one row per step. */
export function bridgeStepColumns() {
  return [
    col.text('label', 'Step'),
    col.hours('signed_hours', 'Hours'),
    col.hours('after', 'Running total'),
    col.percent('share_of_gross', 'Share of gross'),
    col.text('group', 'Contributors'),
  ];
}

/**
 * The contributors behind one step.
 * @param {{ group?: 'person' | 'project' | 'opportunity' | 'service', step?: string }} opts
 */
export function bridgeContributorColumns({ group = 'service', step = null } = {}) {
  const hours = col.hours('hours', 'Hours');
  const share = col.percent('share', 'Share');
  if (group === 'person')
    return [
      col.text('label', 'Person'),
      col.text('department', 'Department'),
      col.text('canonical_service', 'Service'),
      hours,
      share,
      ...(step === 'time_off'
        ? [
            col.hours('holiday_hours', 'Holiday'),
            col.hours('pto_hours', 'PTO'),
            col.hours('reduced_hours', 'Reduced'),
          ]
        : []),
      col.hours('gross_hours', 'Gross'),
      col.hours('net_hours', 'Net'),
      col.count('weeks', 'Weeks'),
    ];
  if (group === 'project')
    return [
      col.text('label', 'Project'),
      col.text('client', 'Client'),
      col.text('canonical_type', 'Type'),
      hours,
      share,
      col.text('confidence_tier', 'Confidence'),
      col.list('source_modes', 'Source'),
      col.count('people', 'People'),
      col.count('weeks', 'Weeks'),
      col.flag('slipped', 'Slipped'),
    ];
  if (group === 'opportunity')
    return [
      col.text('label', 'Deal'),
      col.text('client', 'Client'),
      col.text('stage_name', 'Stage'),
      col.percent('probability_used', 'Probability'),
      col.date('close_date_adjusted', 'Close (adjusted)'),
      hours,
      share,
      col.count('weeks', 'Weeks'),
    ];
  return [
    col.text('label', 'Service'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('demand_hours', 'Demand'),
    col.hours('headroom_hours', 'Headroom'),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.count('people', 'People'),
  ];
}
